import { Injectable } from "@angular/core";
import * as signalR from "@microsoft/signalr";
import { HubConnection, HubConnectionState, IHttpConnectionOptions, LogLevel } from "@microsoft/signalr";
import { AuthService } from "./auth.service";
import { filter, take } from "rxjs/operators";

/** Service to handle SignalR for pushing messages from backend */
@Injectable({
  providedIn: "root",
})
export class SignalRService {
  private readonly connections = new Map<string, HubConnection>();

  constructor(private authService: AuthService) {}

  createConnection(url: string, connectFunction: Function[], scope: string[]): void {
    if (!this.authService.getActiveAccount()) {
      this.waitForAccessToken(url, connectFunction, scope);
      return;
    }

    if (this.hasActiveConnection(url)) {
      return;
    }

    const existing = this.connections.get(url);
    if (existing) {
      existing.stop();
      this.connections.delete(url);
    }

    const options: IHttpConnectionOptions = {
      accessTokenFactory: () => {
        const result = this.authService.getApiAccessToken(scope)
          .pipe(filter((token) => !!token))
          .toPromise();

        return result.then((token) => token ?? "");
      }
    };

    const connection = new signalR.HubConnectionBuilder()
      .withAutomaticReconnect()
      .configureLogging({
        log: (logLevel, message) => {
          if (logLevel < LogLevel.Error) {
            return;
          }
          if (this.isExpectedConnectionFailure(message)) {
            return;
          }
          console.error(message);
        },
      })
      .withUrl(url, options)
      .build();

    this.connections.set(url, connection);

    connection
      .start()
      .then(() => {
        for (let i = 0; i < connectFunction.length; i++) {
          this.bindConnectionMessage(connection, connectFunction[i]);
        }
      })
      .catch((error) => {
        console.warn(`SignalR could not connect to ${url}: ${error.message}`);
      });
  }

  private isExpectedConnectionFailure(message: string): boolean {
    return (
      message.includes("Failed to complete negotiation") ||
      message.includes("Failed to start the connection") ||
      message.includes("Azure SignalR Service is not connected")
    );
  }

  private hasActiveConnection(url: string): boolean {
    const connection = this.connections.get(url);
    if (!connection) {
      return false;
    }

    return (
      connection.state === HubConnectionState.Connected ||
      connection.state === HubConnectionState.Connecting ||
      connection.state === HubConnectionState.Reconnecting
    );
  }

  private waitForAccessToken(url: string, connectFunction: Function[], scope: string[]): void {
    this.authService.accessTokenChanged
      .pipe(
        filter((token) => !!token),
        take(1)
      )
      .subscribe(() => {
        this.createConnection(url, connectFunction, scope);
      });
  }

  private bindConnectionMessage(
    connection: signalR.HubConnection,
    connectFunction: Function
  ) {
    connection.on(connectFunction.name.replace("bound ", ""), (args) =>
      connectFunction(args)
    );
  }
}
